import premiumRules from '../../shared/premium';
import { observePremiumExpiration } from './services/premiumExpiration';
import { Routes, Route } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useAuthStore } from './stores/authStore';
import Layout from './components/Layout';
import LoadingScreen from './components/LoadingScreen';
import ProtectedRoute from './components/ProtectedRoute';
import RoomGuard from './components/RoomGuard';
import { FEATURES } from './config/features';
import ComingSoon from './pages/ComingSoon';

// Lazy loading des pages
const Premium = lazy(() => import('./pages/Premium'));
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Games = lazy(() => import('./pages/Games'));
const Lobby = lazy(() => import('./pages/Lobby'));
const Game = lazy(() => import('./pages/Game'));
const BlitzMode = lazy(() => import('./pages/BlitzMode'));
const SoloMode = lazy(() => import('./pages/SoloMode'));
const Play = lazy(() => import('./pages/Play'));
const Profile = lazy(() => import('./pages/Profile'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Settings = lazy(() => import('./pages/Settings'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Tournaments = lazy(() => import('./pages/Tournaments'));
const TournamentDetail = lazy(() => import('./pages/Tournaments').then(m => ({ default: m.TournamentDetail })));
const RouletteMode = lazy(() => import('./pages/RouletteMode'));
const Clans = lazy(() => import('./pages/Clans'));
const ClanDetail = lazy(() => import('./pages/Clans').then(m => ({ default: m.ClanDetail })));
const Friends = lazy(() => import('./pages/Friends'));
const BattlePass = lazy(() => import('./pages/BattlePass'));
const PuzzleMode = lazy(() => import('./pages/PuzzleMode'));
const CinemaMode = lazy(() => import('./pages/CinemaMode'));
const Replays = lazy(() => import('./pages/Replays'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Community = lazy(() => import('./pages/Community'));
const Spectate = lazy(() => import('./pages/Spectate'));
const SpectatorView = lazy(() => import('./pages/Spectate').then(m => ({ default: m.SpectatorView })));

const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));

// Admin pages
const AvatarMaker = lazy(() => import('./pages/admin/AvatarMaker'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));

function App() {
  const { checkAuth, isLoading, user } = useAuthStore();
  
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  
  useEffect(() => observePremiumExpiration(user, () => {
    const current = useAuthStore.getState().user;
    if (current?.premium && !premiumRules.isPremiumActive(current.premium, current.premiumExpiry)) {
      useAuthStore.getState().setUser({ ...current, premium: false });
    }
  }), [user?.id, user?.premium, user?.premiumExpiry]);

  if (isLoading) {
    return <LoadingScreen />;
  }
  
  return (
    <Suspense fallback={<LoadingScreen />}>
      <RoomGuard />
      <Routes>
        {/* Routes publiques */}
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="play" element={<Play />} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route path="leaderboard" element={<Leaderboard />} />
          <Route path="premium" element={<Premium />} />
          
          {/* Routes protégées */}
          <Route element={<ProtectedRoute />}>
            <Route path="games" element={<Games />} />
            <Route path="solo" element={<SoloMode />} />
            <Route path="roulette" element={FEATURES.roulette ? <RouletteMode /> : <ComingSoon />} />
            <Route path="tournaments" element={FEATURES.tournaments ? <Tournaments /> : <ComingSoon />} />
            <Route path="tournaments/:id" element={FEATURES.tournaments ? <TournamentDetail /> : <ComingSoon />} />
            <Route path="clans" element={FEATURES.clans ? <Clans /> : <ComingSoon />} />
            <Route path="clans/:id" element={FEATURES.clans ? <ClanDetail /> : <ComingSoon />} />
            <Route path="friends" element={FEATURES.friends ? <Friends /> : <ComingSoon />} />
            <Route path="battlepass" element={FEATURES.battlePass ? <BattlePass /> : <ComingSoon />} />
            <Route path="puzzle" element={FEATURES.puzzle ? <PuzzleMode /> : <ComingSoon />} />
            <Route path="cinema" element={FEATURES.cinema ? <CinemaMode /> : <ComingSoon />} />
            <Route path="replays" element={FEATURES.replays ? <Replays /> : <ComingSoon />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="community" element={FEATURES.community ? <Community /> : <ComingSoon />} />
            <Route path="spectate" element={FEATURES.spectate ? <Spectate /> : <ComingSoon />} />
            <Route path="spectate/:gameId" element={FEATURES.spectate ? <SpectatorView /> : <ComingSoon />} />
            <Route path="lobby" element={<Lobby />} />
            <Route path="lobby/:roomCode" element={<Lobby />} />
            <Route path="game/:gameId" element={<Game />} />
            <Route path="blitz" element={FEATURES.blitz ? <BlitzMode /> : <ComingSoon />} />
            <Route path="profile" element={<Profile />} />
            <Route path="profile/:userId" element={<Profile />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          
          {/* Routes Admin */}
          <Route element={<ProtectedRoute requireAdmin />}>
            <Route path="admin/avatar-maker" element={<AvatarMaker />} />
          </Route>
        </Route>

        {/* Admin Dashboard (public route with own API key auth) */}
        <Route path="admin" element={<AdminDashboard />} />
        
        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default App;
